export type Migration = (raw: any) => any;

const LEGACY_INLINE_FUSE_HOLDER_IDS = new Set([
  "dist-fuse-inline",
  "dist-midi",
  "dist-anl",
  "dist-class-t-200",
  "dist-class-t-300",
  "dist-class-t-400",
  "dist-bs-mega-300",
  "dist-bs-mrbf",
]);

function isLegacyInlineFuseHolder(node: any): boolean {
  if (!node || node.kind !== "component") return false;
  if (LEGACY_INLINE_FUSE_HOLDER_IDS.has(node.definitionId)) return true;
  return !!node.customDefinition?.isFuseHolder;
}

/**
 * v1 → v2: delete inline fuse-holder nodes; their two cables merge into one
 * carrying the holder's rating as `fuseAmpOverride`.
 */
function migrateV1ToV2(s: any): any {
  const holders: any[] = [];
  for (const n of s.nodes) {
    if (isLegacyInlineFuseHolder(n)) holders.push(n);
  }
  if (holders.length === 0) return s;

  const cables: any[] = s.cables.map((c: any) => ({ ...c }));
  const removeCableIds = new Set<string>();
  const removeNodeIds = new Set<string>();

  for (const holder of holders) {
    removeNodeIds.add(holder.id);
    const attached = cables.filter(
      (c) =>
        !removeCableIds.has(c.id) &&
        (c.fromNodeId === holder.id || c.toNodeId === holder.id),
    );
    const fuseAmp = holder.fuseRating ?? null;

    if (attached.length === 2) {
      const [a, b] = attached;
      const aFarNodeId = a.fromNodeId === holder.id ? a.toNodeId : a.fromNodeId;
      const aFarTermId = a.fromNodeId === holder.id ? a.toTerminalId : a.fromTerminalId;
      const bFarNodeId = b.fromNodeId === holder.id ? b.toNodeId : b.fromNodeId;
      const bFarTermId = b.fromNodeId === holder.id ? b.toTerminalId : b.fromTerminalId;
      a.fromNodeId = aFarNodeId;
      a.fromTerminalId = aFarTermId;
      a.toNodeId = bFarNodeId;
      a.toTerminalId = bFarTermId;
      a.lengthM = (a.lengthM ?? 0) + (b.lengthM ?? 0);
      if (fuseAmp != null && a.fuseAmpOverride == null) a.fuseAmpOverride = fuseAmp;
      removeCableIds.add(b.id);
    } else {
      for (const c of attached) {
        if (fuseAmp != null && c.fuseAmpOverride == null) {
          c.fuseAmpOverride = fuseAmp;
        }
        if (attached.length === 1) removeCableIds.add(c.id);
      }
    }
  }

  return {
    ...s,
    nodes: s.nodes.filter((n: any) => !removeNodeIds.has(n.id)),
    cables: cables.filter((c) => !removeCableIds.has(c.id)),
  };
}

/** v2 → v3: `kind: "panel"` → `kind: "zone"`, and seed `zoneDistances: {}`. */
function migrateV2ToV3(s: any): any {
  return {
    ...s,
    nodes: s.nodes.map((n: any) =>
      n && n.kind === "panel" ? { ...n, kind: "zone" } : n,
    ),
    zoneDistances: s.zoneDistances ?? {},
  };
}

/**
 * v3 → v4: a retired id becomes a `customDefinition` whose `id` names the
 * surviving sibling, so "Reset specs" still has a target.
 */

const RETIRED_DEFS: Record<string, { surviveAs: string; def: any }> = {
  "batt-lifepo4-200": {
    surviveAs: "batt-lifepo4-100",
    def: {
      id: "batt-lifepo4-100",
      name: "200 Ah LiFePO4",
      shortName: "LiFePO4 200Ah",
      subtitle: "Leisure battery",
      category: "battery",
      description: "Generic 12V 200Ah lithium iron phosphate house battery.",
      capacityAh: 200,
      voltage: 12.8,
      chemistry: "lifepo4",
      maxDischargeA: 200,
      maxChargeA: 100,
      width: 200,
      height: 80,
      terminals: [
        { id: "pos", label: "+", kind: "positive", side: "left", offset: 0.5, maxAmp: 300 },
        { id: "neg", label: "−", kind: "negative", side: "right", offset: 0.5, maxAmp: 300 },
      ],
    },
  },
  "solar-200": {
    surviveAs: "solar-100",
    def: {
      id: "solar-100",
      name: "200 W Solar Panel",
      shortName: "200W Panel",
      subtitle: "Solar panel 200W",
      category: "solar",
      watts: 200,
      voltage: 24,
      ampOutput: 8.5,
      peakSunHoursDefault: 4,
      width: 200,
      height: 110,
      terminals: [
        { id: "pos", label: "+", kind: "positive", side: "bottom", offset: 0.35, maxAmp: 15 },
        { id: "neg", label: "−", kind: "negative", side: "bottom", offset: 0.65, maxAmp: 15 },
      ],
    },
  },
};

function migrateV3ToV4(s: any): any {
  let touched = false;
  const nodes = s.nodes.map((n: any) => {
    if (!n || n.kind !== "component") return n;

    const retired = RETIRED_DEFS[n.definitionId];
    if (retired) {
      touched = true;
      return {
        ...n,
        definitionId: "custom",
        customDefinition: retired.def,
      };
    }

    if (n.definitionId === "custom" && n.customDefinition) {
      const customRetired = RETIRED_DEFS[n.customDefinition.id];
      if (customRetired) {
        touched = true;
        return {
          ...n,
          customDefinition: { ...n.customDefinition, id: customRetired.surviveAs },
        };
      }
    }

    return n;
  });

  return touched ? { ...s, nodes } : s;
}

/** v4 → v5: drop the per-cable `userEdited` flag. Waypoints are kept. */
function migrateV4ToV5(s: any): any {
  let touched = false;
  const cables = s.cables.map((c: any) => {
    if (c && "userEdited" in c) {
      touched = true;
      const { userEdited: _ue, ...rest } = c;
      return rest;
    }
    return c;
  });
  return touched ? { ...s, cables } : s;
}

/**
 * v5 → v6: junction nodes become components. Their cables reference terminal id
 * `"j"`, which the junction catalog entry has to keep exposing.
 */
function migrateV5ToV6(s: any): any {
  let touched = false;
  const nodes = s.nodes.map((n: any) => {
    if (!n || n.kind !== "junction") return n;
    touched = true;
    const upgraded: any = {
      id: n.id,
      kind: "component",
      definitionId: "junction",
      position: n.position,
    };
    if (n.parentId) upgraded.parentId = n.parentId;
    if (n.label) upgraded.label = n.label;
    if (n.provisional) upgraded.provisional = true;
    return upgraded;
  });
  return touched ? { ...s, nodes } : s;
}

/**
 * v6 → v7: hoist each slot's `terminalFuses` rating onto the cable wired to it.
 * Ids are frozen here, not read from the live catalog, whose churn would break
 * old payloads.
 */
const FUSE_BLOCK_IDS = new Set([
  "dist-fuse-block-6",
  "dist-fuse-block-12",
  "dist-victron-lynx-distributor",
  "fuse-block-6",
  "fuse-block-12",
]);

function isFuseBlockNode(node: any): boolean {
  if (!node || node.kind !== "component") return false;
  if (FUSE_BLOCK_IDS.has(node.definitionId)) return true;
  return !!node.customDefinition?.isFuseBlock;
}

function migrateV6ToV7(s: any): any {
  let touched = false;
  const cables: any[] = s.cables.map((c: any) => ({ ...c }));

  const nodes = s.nodes.map((n: any) => {
    if (!isFuseBlockNode(n)) return n;
    if (!n.terminalFuses) return n;
    touched = true;
    const tf = n.terminalFuses as Record<string, number>;
    for (const [slotId, rating] of Object.entries(tf)) {
      if (rating == null) continue;
      for (const c of cables) {
        const onSlot =
          (c.fromNodeId === n.id && c.fromTerminalId === slotId) ||
          (c.toNodeId === n.id && c.toTerminalId === slotId);
        if (!onSlot) continue;
        if (c.fuseAmpOverride == null) c.fuseAmpOverride = rating;
      }
    }
    const { terminalFuses: _tf, ...rest } = n;
    return rest;
  });

  return touched ? { ...s, nodes, cables } : s;
}

/**
 * v7 → v8: `showFusesOnBar` → `isFusebox` on busbar nodes; dropped when unset.
 */
function migrateV7ToV8(s: any): any {
  let touched = false;
  const nodes = s.nodes.map((n: any) => {
    if (!n || n.kind !== "component") return n;
    if (!("showFusesOnBar" in n)) return n;
    touched = true;
    const { showFusesOnBar, ...rest } = n;
    return showFusesOnBar ? { ...rest, isFusebox: true } : rest;
  });
  return touched ? { ...s, nodes } : s;
}

/**
 * v8 → v9: card fuse blocks become `dist-pos-bus` busbars. Slots map onto the
 * stud id pool `busbarStudList` uses, so cable references stay valid; `in-neg`
 * moves to a new junction, since the bar is positive-only. The card dimensions
 * below are baked in — the catalog entries are gone after this version.
 */
const FUSE_BLOCK_CARD_WIDTH: Record<string, { width: number; height: number; studs: number }> = {
  "dist-fuse-block-6": { width: 220, height: 160, studs: 6 },
  "dist-fuse-block-12": { width: 240, height: 220, studs: 12 },
};
const POS_BUS_CATALOG_STUDS = ["b", "c", "d", "e", "f"];
function slotIdToStudId(slotId: string): string | null {
  const m = /^c(\d+)$/.exec(slotId);
  if (!m) return null;
  const idx = parseInt(m[1], 10) - 1;
  if (idx < 0) return null;
  return POS_BUS_CATALOG_STUDS[idx] ?? `stud-${idx + 1}`;
}
function migrateV8ToV9(s: any): any {
  const targets = (s.nodes ?? []).filter(
    (n: any) =>
      n && n.kind === "component" && FUSE_BLOCK_CARD_WIDTH[n.definitionId],
  );
  if (targets.length === 0) return s;

  const cables: any[] = (s.cables ?? []).map((c: any) => ({ ...c }));
  const newJunctions: any[] = [];

  const convertedNodes = (s.nodes ?? []).map((n: any) => {
    const meta = n && n.kind === "component" ? FUSE_BLOCK_CARD_WIDTH[n.definitionId] : undefined;
    if (!meta) return n;
    const { studCount: _existingStudCount, ...rest } = n;
    return {
      ...rest,
      definitionId: "dist-pos-bus",
      isFusebox: true,
      studCount: meta.studs,
    };
  });

  for (const target of targets) {
    const meta = FUSE_BLOCK_CARD_WIDTH[target.definitionId];
    let junctionId: string | null = null;
    const ensureJunction = () => {
      if (junctionId) return junctionId;
      junctionId = `${target.id}__neg-junction`;
      newJunctions.push({
        id: junctionId,
        kind: "component",
        definitionId: "junction",
        // IN− sat at left, offset 0.4; nudge the 14×14 junction clear of the bar.
        position: {
          x: target.position.x - 24,
          y: target.position.y + meta.height * 0.4 - 7,
        },
        ...(target.parentId ? { parentId: target.parentId } : {}),
      });
      return junctionId;
    };

    for (const c of cables) {
      const onFromSide = c.fromNodeId === target.id;
      const onToSide = c.toNodeId === target.id;
      if (!onFromSide && !onToSide) continue;
      const termId = onFromSide ? c.fromTerminalId : c.toTerminalId;

      if (termId === "in-pos") {
        if (onFromSide) c.fromTerminalId = "a";
        else c.toTerminalId = "a";
        c.waypoints = undefined;
        continue;
      }
      if (termId === "in-neg") {
        const jid = ensureJunction();
        if (onFromSide) {
          c.fromNodeId = jid;
          c.fromTerminalId = "j";
        } else {
          c.toNodeId = jid;
          c.toTerminalId = "j";
        }
        c.waypoints = undefined;
        continue;
      }
      const studId = slotIdToStudId(termId);
      if (studId == null) continue;
      if (onFromSide) c.fromTerminalId = studId;
      else c.toTerminalId = studId;
      c.waypoints = undefined;
    }
  }

  return {
    ...s,
    nodes: [...convertedNodes, ...newJunctions],
    cables,
  };
}

/**
 * v9 → v10: `Terminal.kind` splits into `polarity` and `direction`. Only
 * `customDefinition.terminals` need it — the catalog ships the new shape.
 */
const V10_POS_HINT = /(^|[\s_-])(pos|in-pos|out-pos|bat\+|batt\+|b\+|alt\+|pv\+|\+)/i;
const V10_NEG_HINT = /(^|[\s_-])(neg|in-neg|out-neg|bat-|batt-|b-|alt-)/i;
const V10_PV_NEG_HINT = /pv-(?!pos)/i;
const V10_GND_HINT = /\b(gnd|ground|earth|chassis)\b/i;

function inferV10Polarity(id: string, label: string): "positive" | "negative" | "ground" | null {
  const text = `${id} ${label}`;
  if (V10_GND_HINT.test(text)) return "ground";
  const hasPos = V10_POS_HINT.test(text) || label.includes("+");
  const hasNeg =
    V10_NEG_HINT.test(text) ||
    V10_PV_NEG_HINT.test(text) ||
    label.includes("−") ||
    label.trim() === "-";
  if (hasPos) return "positive"; // positive wins ties (e.g. "pv-pos" matches both)
  if (hasNeg) return "negative";
  return null;
}

function migrateTerminalV9ToV10(t: any, isFuseBlock: boolean): any {
  if (!t || typeof t !== "object") return t;
  if (typeof t.polarity === "string" && typeof t.direction === "string") return t;
  const kind = t.kind;
  let polarity: string;
  let direction: string;
  if (kind === "positive" || kind === "negative" || kind === "ground" || kind === "signal") {
    polarity = kind;
    direction = "bidi";
  } else if (kind === "input" || kind === "output") {
    direction = kind;
    const inferred = inferV10Polarity(t.id ?? "", t.label ?? "");
    if (inferred) polarity = inferred;
    else if (isFuseBlock && kind === "output") polarity = "positive";
    else polarity = "neutral";
  } else {
    polarity = "neutral";
    direction = "bidi";
  }
  const { kind: _drop, ...rest } = t;
  return { ...rest, polarity, direction };
}

function migrateDefV9ToV10(def: any): any {
  if (!def || !Array.isArray(def.terminals)) return def;
  const isFuseBlock = !!def.isFuseBlock;
  return {
    ...def,
    terminals: def.terminals.map((t: any) => migrateTerminalV9ToV10(t, isFuseBlock)),
  };
}

function migrateV9ToV10(s: any): any {
  if (!s || !Array.isArray(s.nodes)) return s;
  const nodes = s.nodes.map((n: any) => {
    if (!n || n.kind !== "component" || !n.customDefinition) return n;
    return { ...n, customDefinition: migrateDefV9ToV10(n.customDefinition) };
  });
  return { ...s, nodes };
}

/**
 * v10 → v11: `isInlineFuseHolder` folds into `isFuseBlock`. Only custom
 * payloads that inlined the flag need it; catalog nodes resolve the new entry.
 */
function migrateDefV10ToV11(def: any): any {
  if (!def || typeof def !== "object") return def;
  if (!def.isInlineFuseHolder) return def;
  const { isInlineFuseHolder: _drop, ...rest } = def;
  return { ...rest, isFuseBlock: true };
}

function migrateV10ToV11(s: any): any {
  if (!s || !Array.isArray(s.nodes)) return s;
  const nodes = s.nodes.map((n: any) => {
    if (!n || n.kind !== "component" || !n.customDefinition) return n;
    return { ...n, customDefinition: migrateDefV10ToV11(n.customDefinition) };
  });
  return { ...s, nodes };
}

/**
 * v11 → v12: symbol-variant parts draw their leads at fixed positions, so stale
 * `terminalOverrides` and waypoints are stripped. Ids are frozen here, not read
 * from the live catalog, which churns.
 */
const V11_SYMBOL_VARIANT_IDS = new Set([
  "dist-holder-mega",
  "dist-holder-anl",
  "dist-holder-classt",
  "dist-holder-mrbf",
  "dist-main-switch",
  "dist-solar-dp-isolator",
  "dist-bs-mseries-6005",
  "dist-relay-200",
  "chassis-ground-symbol",
]);

function isV11SymbolVariantNode(n: any): boolean {
  if (!n || n.kind !== "component") return false;
  if (V11_SYMBOL_VARIANT_IDS.has(n.definitionId)) return true;
  const rv = n.customDefinition?.renderVariant;
  return rv === "fuse-holder" || rv === "switch" || rv === "chassis-ground";
}

function migrateV11ToV12(s: any): any {
  if (!s || !Array.isArray(s.nodes)) return s;
  const symbolNodeIds = new Set<string>();
  const nodes = s.nodes.map((n: any) => {
    if (!isV11SymbolVariantNode(n)) return n;
    symbolNodeIds.add(n.id);
    if (!n.terminalOverrides) return n;
    const { terminalOverrides: _drop, ...rest } = n;
    return rest;
  });
  if (symbolNodeIds.size === 0) return { ...s, nodes };
  const cables = Array.isArray(s.cables)
    ? s.cables.map((c: any) => {
        if (!c || !c.waypoints || c.waypoints.length === 0) return c;
        const touchesSymbol =
          symbolNodeIds.has(c.fromNodeId) || symbolNodeIds.has(c.toNodeId);
        if (!touchesSymbol) return c;
        const { waypoints: _drop, ...rest } = c;
        return rest;
      })
    : s.cables;
  return { ...s, nodes, cables };
}

/**
 * v12 → v13: null a `fuseAmpOverride` that mirrors an endpoint battery's
 * `batteryFuse.ratingA`. The inheritance walk already surfaces that fuse, and
 * the mirror makes `detectFuseExceedsWire` report one that isn't there.
 */
function migrateV12ToV13(s: any): any {
  if (!s || !Array.isArray(s.cables) || !Array.isArray(s.nodes)) return s;
  const nodesById: Record<string, any> = {};
  for (const n of s.nodes) if (n && typeof n.id === "string") nodesById[n.id] = n;
  let touched = false;
  const cables = s.cables.map((c: any) => {
    if (!c || c.fuseAmpOverride == null) return c;
    const a = nodesById[c.fromNodeId];
    const b = nodesById[c.toNodeId];
    const mirrors =
      a?.batteryFuse?.ratingA === c.fuseAmpOverride ||
      b?.batteryFuse?.ratingA === c.fuseAmpOverride;
    if (!mirrors) return c;
    touched = true;
    const next: any = { ...c, fuseAmpOverride: null };
    if (next.fuseTypeOverride != null) next.fuseTypeOverride = null;
    return next;
  });
  return touched ? { ...s, cables } : s;
}

/**
 * v13 → v14: strip AI-provenance fields from inline custom definitions. Not a
 * no-op: a v13 file cannot hold a generated component, so a payload wearing
 * those keys falsely marks its fields as estimates in the inspector.
 */
function migrateV13ToV14(s: any): any {
  if (!s || !Array.isArray(s.nodes)) return s;
  let touched = false;
  const nodes = s.nodes.map((n: any) => {
    const def = n?.customDefinition;
    if (!def || typeof def !== "object") return n;
    if (
      def.source === undefined &&
      def.aiEstimatedFields === undefined &&
      def.aiAssumptions === undefined
    ) {
      return n;
    }
    touched = true;
    const nextDef = { ...def };
    delete nextDef.source;
    delete nextDef.aiEstimatedFields;
    delete nextDef.aiAssumptions;
    return { ...n, customDefinition: nextDef };
  });
  return touched ? { ...s, nodes } : s;
}

/**
 * v14 → v15: strip `Cable.lengthEstimated` and `ComponentNode.partEstimated`.
 * Not a no-op: a v14 file cannot hold either key, so a payload wearing one
 * marks the user's own measurements as unverified.
 */
function migrateV14ToV15(s: any): any {
  if (!s) return s;
  let touched = false;
  const nodes = Array.isArray(s.nodes)
    ? s.nodes.map((n: any) => {
        if (!n || typeof n !== "object" || n.partEstimated === undefined) return n;
        touched = true;
        const next = { ...n };
        delete next.partEstimated;
        return next;
      })
    : s.nodes;
  const cables = Array.isArray(s.cables)
    ? s.cables.map((c: any) => {
        if (!c || typeof c !== "object" || c.lengthEstimated === undefined) return c;
        touched = true;
        const next = { ...c };
        delete next.lengthEstimated;
        return next;
      })
    : s.cables;
  return touched ? { ...s, nodes, cables } : s;
}

/** v15 → v16: preserve the known one-way topology in edited copies of
 * catalog isolators. Only backfill when the original terminal identities and
 * directions remain; unrelated/custom rewired definitions are not guessed. */
function migrateV15ToV16(s: any): any {
  if (!s || !Array.isArray(s.nodes)) return s;
  const pairs: Record<string, string[][]> = {
    "dist-battery-isolator-200": [["alt-in", "batt1-out"], ["alt-in", "batt2-out"]],
    "dist-victron-argofet-100-2": [["alt-in", "out1"], ["alt-in", "out2"]],
    "dist-victron-argofet-100-3": [["alt-in", "out1"], ["alt-in", "out2"], ["alt-in", "out3"]],
  };
  let touched = false;
  const nodes = s.nodes.map((n: any) => {
    const d = n?.customDefinition;
    if (n?.definitionId !== "custom" || d?.category !== "distribution" ||
        d.oneWayDcConnections !== undefined || !Array.isArray(d.terminals)) return n;
    const expected = Object.hasOwn(pairs, d.id) ? pairs[d.id] : undefined;
    if (!expected || !expected.every(([a, b]) =>
      d.terminals.some((t: any) => t?.id === a && t.polarity === "positive" && t.direction === "input") &&
      d.terminals.some((t: any) => t?.id === b && t.polarity === "positive" && t.direction === "output"))) return n;
    touched = true;
    return { ...n, customDefinition: { ...d, oneWayDcConnections: expected.map(pair => [...pair]) } };
  });
  return touched ? { ...s, nodes } : s;
}

/** v16 → v17: carry reviewed electronic output limits into intact historical
 * custom copies. Frozen profiles deliberately do not import the mutable catalog.
 * Identity, electrical fields and every terminal must still match; presentation
 * edits are harmless, but overridden ratings/topology never inherit a ceiling.
 * Sources: Victron Smart IP43 120–240 V manual §4.7; no.co/genpro10x2. */
function migrateV16ToV17(s: any): any {
  if (!s || !Array.isArray(s.nodes)) return s;
  const profiles: Record<string, any> = {
    "shore-victron-ip43-30-1plus1": {
      manufacturer: "Victron", model: "Phoenix Smart IP43 12/30 (1+1)",
      ampOutput: 30, chargeAmpsTypical: 28, compatibleVoltages: [12],
      limits: { "batt-pos": 30, "start-pos": 4 },
      terminals: [
        ["ac-l", "neutral", "input", 16], ["ac-n", "neutral", "input", 16], ["ac-pe", "ground", "bidi", 16],
        ["batt-pos", "positive", "output", 30], ["batt-neg", "negative", "output", 30],
        ["start-pos", "positive", "output", 4], ["start-neg", "negative", "output", 4],
      ],
    },
    "shore-noco-genpro10x2": {
      manufacturer: "NOCO", model: "GENPRO10X2", voltage: 12,
      ampOutput: 20, compatibleVoltages: [12],
      limits: { "bank1-pos": 10, "bank2-pos": 10 },
      terminals: [
        ["ac-in-l", "neutral", "input", 2.5], ["ac-in-n", "neutral", "input", 2.5],
        ["bank1-pos", "positive", "output", 10], ["bank1-neg", "negative", "output", 10],
        ["bank2-pos", "positive", "output", 10], ["bank2-neg", "negative", "output", 10],
      ],
    },
  };
  const electricalFields = ["ampOutput", "chargeAmpsTypical", "voltage", "compatibleVoltages", "ampDraw", "ampSurge",
    "watts", "inputVoltage", "outputVoltage", "maxInputA", "conversionEfficiency", "hybridSourceOutputLimitA",
    "pvMaxVoc", "pvVoc", "pvMaxIsc", "internalDcConnections", "oneWayDcConnections", "acVoltage", "simulateStates", "settings"];
  let touched = false;
  const nodes = s.nodes.map((n: any) => {
    const d = n?.customDefinition;
    if (n?.kind !== "component" || n.definitionId !== "custom" || d?.category !== "charger" ||
        d.outputCurrentLimitsA !== undefined || !Array.isArray(d.terminals)) return n;
    const profile = typeof d.id === "string" && Object.hasOwn(profiles, d.id) ? profiles[d.id] : undefined;
    if (!profile || d.manufacturer !== profile.manufacturer || d.model !== profile.model ||
        electricalFields.some(key => JSON.stringify(d[key]) !== JSON.stringify(profile[key])) ||
        d.terminals.length !== profile.terminals.length ||
        !profile.terminals.every(([id, polarity, direction, maxAmp]: any[]) =>
          d.terminals.filter((t: any) => t?.id === id && t.polarity === polarity && t.direction === direction && t.maxAmp === maxAmp).length === 1)) return n;
    touched = true;
    return { ...n, customDefinition: { ...d, outputCurrentLimitsA: { ...profile.limits } } };
  });
  return touched ? { ...s, nodes } : s;
}

/** v21 → v22: add the omitted drawing grounds to saved Magnum catalog copies.
 * Labels, ratings, positions and existing wiring remain the user's own. This
 * adds no internal ground/neutral/DC-negative bond. Only the complete legacy
 * terminal layout qualifies; rewired or partially grounded copies are left alone.
 * MSH3012RV manual 64-0089 Rev E §§1.3, 2.5.4: two AC grounds and a DC chassis lug.
 * https://www.sensata.com/sites/default/files/a/sensata-msh-3012rv-inverter-charger-manual.pdf */
function migrateV21ToV22(s: any): any {
  if (!s || !Array.isArray(s.nodes)) return s;
  const models: Record<string, string> = {
    "shore-magnum-msh3012rv": "MSH3012RV",
    "shore-magnum-msh3012rv-l": "MSH3012RV-L",
  };
  const legacyTerminals = [
    ["ac-in-l1", ["neutral", "positive"], "input"],
    ["ac-in-l2", ["neutral", "positive"], "input"],
    ["ac-in-n", ["neutral"], "input"],
    ["dc-pos", ["positive"], "input"],
    ["dc-neg", ["negative"], "input"],
    ["ac-out-l1", ["neutral", "positive"], "output"],
    ["ac-out-l2", ["neutral", "positive"], "output"],
    ["ac-out-n", ["neutral"], "output"],
    ["network", ["signal"], "bidi"],
  ] as const;
  let touched = false;
  const nodes = s.nodes.map((n: any) => {
    const d = n?.customDefinition;
    if (n?.kind !== "component" || n.definitionId !== "custom" || d?.category !== "charger" ||
        typeof d.id !== "string" || !Object.hasOwn(models, d.id) ||
        d.manufacturer !== "Magnum Energy" || d.model !== models[d.id] ||
        d.voltage !== 12 || d.acVoltage !== 120 || d.requestedModel !== undefined ||
        d.internalDcConnections !== undefined || d.oneWayDcConnections !== undefined ||
        d.simulateStates !== undefined || !Array.isArray(d.terminals) ||
        d.terminals.length !== legacyTerminals.length ||
        !legacyTerminals.every(([id, polarities, direction]) =>
          d.terminals.filter((t: any) => t?.id === id &&
            (polarities as readonly string[]).includes(t.polarity) && t.direction === direction &&
            typeof t.maxAmp === "number" && Number.isFinite(t.maxAmp) && t.maxAmp >= 0).length === 1)) return n;
    touched = true;
    return { ...n, customDefinition: { ...d, terminals: [...d.terminals,
      { id: "ac-in-pe", label: "AC IN PE", polarity: "ground", direction: "bidi", side: "top", offset: 0.15, maxAmp: 0 },
      { id: "chassis", label: "CHASSIS", polarity: "ground", direction: "bidi", side: "top", offset: 0.5, maxAmp: 0 },
      { id: "ac-out-pe", label: "AC OUT PE", polarity: "ground", direction: "bidi", side: "top", offset: 0.85, maxAmp: 0 },
    ] } };
  });
  return touched ? { ...s, nodes } : s;
}

/** Keyed by *from* version. `MIGRATIONS[N]` upgrades v(N) → v(N+1). */
export const MIGRATIONS: Record<number, Migration> = {
  1: migrateV1ToV2,
  2: migrateV2ToV3,
  3: migrateV3ToV4,
  4: migrateV4ToV5,
  5: migrateV5ToV6,
  6: migrateV6ToV7,
  7: migrateV7ToV8,
  8: migrateV8ToV9,
  9: migrateV9ToV10,
  10: migrateV10ToV11,
  11: migrateV11ToV12,
  12: migrateV12ToV13,
  13: migrateV13ToV14,
  14: migrateV14ToV15,
  15: migrateV15ToV16,
  16: migrateV16ToV17,
  // v18 extends the persisted fuse family with gPV. Existing data needs no
  // rewrite; the version boundary prevents older engines loading new gPV files.
  17: (s) => s,
  // v19 preserves the requested-model marker on generic placeholders. Older
  // apps cannot safely present these as ordinary verified custom components.
  // Existing documents have no placeholders, so no data needs backfilling.
  18: (s) => s,
  // v20 introduces a separate operating charge-current ceiling. Preserve old
  // custom output ratings: their intent cannot be inferred from a number.
  19: (s) => s,
  // v21 persists the selected electrical profile. Missing means the legacy
  // marine rules; never infer a vehicle or standard from a drawing or locale.
  // Older apps must reject new files instead of silently running marine rules.
  20: (s) => s,
  21: migrateV21ToV22,
  // v23 adds the MDL fuse family. Older documents keep their original fuse selections.
  22: (s) => s,
  // v24 records an explicit busbar connection face. Legacy `side` overrides
  // were ignored on busbars and may use the rotated frame; retain that layout.
  23: (s) => s,
};
